import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

const WhatsAppSupport = () => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
    <div className="flex items-start space-x-3">
      <Icon name="Phone" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
      <div>
        <p className="font-medium text-gray-900 mb-2">অথবা সাপোর্ট টিমের সাথে যোগাযোগ করুন (হোয়াটসঅ্যাপ):</p>
        <div className="space-y-2">
          <a href="https://wa.me/8801314951751" target="_blank" rel="noopener noreferrer"
             className="flex items-center text-green-600 hover:text-green-700">
            <span className="text-sm font-mono">+880 1314-951751</span>
            <span className="ml-2 text-xs bg-green-100 px-2 py-1 rounded">WhatsApp</span>
          </a>
          <a href="https://wa.me/8801703788889" target="_blank" rel="noopener noreferrer"
             className="flex items-center text-green-600 hover:text-green-700">
            <span className="text-sm font-mono">+880 1703-788889</span>
            <span className="ml-2 text-xs bg-green-100 px-2 py-1 rounded">WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  </div>
);

const ForgotPassword = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone' | 'call' | 'password' | 'done'
  const [phone, setPhone] = useState('');
  const [resetHandle, setResetHandle] = useState('');
  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentMaybe, setSentMaybe] = useState(false); // response lost on a single-use submit — may have succeeded
  const CALL_IN_NUMBER = '09644342002'; // number users dial for call verification — CONFIRM this is correct

  useEffect(() => {
    if (timeLeft > 0) {
      const t = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [timeLeft]);

  useEffect(() => {
    // Code expired → close the call screen.
    if (step === 'call' && timeLeft === 0) {
      setStep('phone');
      setCode('');
      setError('কোডের মেয়াদ শেষ হয়ে গেছে। অনুগ্রহ করে আবার চেষ্টা করুন।');
    }
  }, [step, timeLeft]);

  useEffect(() => {
    if (step !== 'call' || !resetHandle) return;
    const poll = setInterval(async () => {
      try {
        const s = await apiService.getResetStatus(resetHandle);
        if (s.verified) {
          clearInterval(poll);
          setStep('password');
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(poll);
  }, [step, resetHandle]);

  const handleInitiate = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiService.initiateResetCall(phone);
      setResetHandle(r.reset_handle);
      setCode(r.code);
      setTimeLeft(r.expires_in || 300);
      setStep('call');
    } catch (err) {
      setError(err.message || 'অনুরোধ ব্যর্থ হয়েছে। আবার চেষ্টা করুন।');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (newPassword !== confirmPassword) {
      setError('পাসওয়ার্ড দুটি এক নয়।');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiService.completeReset(resetHandle, newPassword, confirmPassword);
      setStep('done');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      // A network-level failure (e.g. "Failed to fetch") means the server may
      // have applied the change already — the reset code is single-use, so a
      // retry would wrongly fail. Treat it as ambiguous and route to login with
      // the new password instead of showing an error that invites re-submits.
      const lostResponse = err instanceof TypeError || err instanceof SyntaxError ||
        /failed to fetch|networkerror|load failed|network request failed/i.test(err?.message || '');
      if (lostResponse) {
        setSentMaybe(true);
        setStep('done');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      } else {
        setError((err.messages && err.messages.join(' ')) || err.message || 'পাসওয়ার্ড পরিবর্তন ব্যর্থ হয়েছে।');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar user={user} onLogout={logout} />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="Shield" className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mb-2">পাসওয়ার্ড ভুলে গেছেন?</h2>

            {error && (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
            )}

            {step === 'phone' && (
              <div>
                <p className="text-gray-600 text-center mb-6 text-sm">
                  আপনার ফোন নম্বর দিন — আমরা কল করে যাচাই করব, তারপর নতুন পাসওয়ার্ড দিতে পারবেন।
                </p>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="আপনার ফোন নম্বর (01XXXXXXXXX)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
                />
                <Button onClick={handleInitiate} disabled={loading || !phone} className="w-full">
                  <Icon name="Phone" className="w-4 h-4 mr-2" />
                  {loading ? 'অনুরোধ পাঠানো হচ্ছে...' : 'কল দিয়ে যাচাই করুন'}
                </Button>
                <WhatsAppSupport />
              </div>
            )}

            {step === 'call' && (
              <div className="text-center">
                {/* Step 1 — call this number (icon marker, no digit to avoid clashing with the keypad '1') */}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 flex items-center justify-center"><Icon name="Phone" size={15} className="text-white" /></span>
                  <span className="text-sm font-semibold text-gray-800">প্রথমে এই নম্বরে কল করুন</span>
                </div>
                <a href={`tel:${CALL_IN_NUMBER}`}
                   className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-2xl font-bold tracking-wide rounded-xl py-3 mb-6 shadow-sm transition">
                  <Icon name="Phone" className="w-6 h-6" /> {CALL_IN_NUMBER}
                </a>
                {/* Step 2 — press the 1 key, then enter the code (distinct boxed digits) */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 flex items-center justify-center"><Icon name="Smartphone" size={15} className="text-white" /></span>
                  <span className="text-sm font-semibold text-gray-800">এরপর মেনুতে <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-100 text-green-700 text-xs font-bold border border-green-300 align-middle">১</span> চাপুন, তারপর এই কোডটি চাপুন</span>
                </div>
                <p className="text-xs text-gray-400 mb-2 text-center">আপনার কোড</p>
                <div className="flex justify-center gap-2 mb-3">
                  {String(code).split('').map((d, i) => (
                    <span key={i} className="w-12 h-14 flex items-center justify-center text-3xl font-bold bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-800">{d}</span>
                  ))}
                </div>
                {timeLeft > 0 && (
                  <p className="text-sm text-gray-600 mb-2">কোডের মেয়াদ বাকি: <span className="font-semibold">{formatTime(timeLeft)}</span></p>
                )}
                <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-xs rounded p-2 mb-4">
                  ⚠️ ফজর ফাইটার কখনো ফোনে এই কোড চাইবে না। কেউ কোড চাইলে দেবেন না।
                </div>
                <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                  যাচাইয়ের জন্য অপেক্ষা করা হচ্ছে...
                </div>
                <Button variant="outline" onClick={() => { setStep('phone'); setCode(''); setTimeLeft(0); }} className="w-full">
                  বাতিল করুন
                </Button>
              </div>
            )}

            {step === 'password' && (
              <div>
                <p className="text-green-700 text-center mb-4 text-sm">✅ যাচাই সম্পন্ন। এখন নতুন পাসওয়ার্ড দিন।</p>
                <div className="relative mb-3">
                  <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="নতুন পাসওয়ার্ড (কমপক্ষে ৮ অক্ষর)" minLength="8"
                    className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>
                <div className="relative mb-4">
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="আবার পাসওয়ার্ড দিন"
                    className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>
                <Button onClick={handleComplete} disabled={loading || !newPassword || !confirmPassword} className="w-full">
                  {loading ? 'সংরক্ষণ করা হচ্ছে...' : 'পাসওয়ার্ড পরিবর্তন করুন'}
                </Button>
              </div>
            )}

            {step === 'done' && (
              <div className="text-center">
                <Icon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-3" />
                {sentMaybe ? (
                  <>
                    <p className="text-gray-800 font-medium">অনুরোধ পাঠানো হয়েছে।</p>
                    <p className="text-sm text-gray-600 mt-1">সংযোগে সাময়িক সমস্যা হয়েছিল। অনুগ্রহ করে নতুন পাসওয়ার্ড দিয়ে লগইন করার চেষ্টা করুন।</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-800 font-medium">পাসওয়ার্ড পরিবর্তন হয়েছে!</p>
                    <p className="text-sm text-gray-600 mt-1">লগইন পেজে নিয়ে যাওয়া হচ্ছে...</p>
                  </>
                )}
              </div>
            )}

            <div className="text-center mt-6">
              <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium text-sm">← লগইনে ফিরে যান</Link>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ForgotPassword;
